import { useEffect, useState } from 'react';
import { FormProvider, type UseFormReturn, useFormState } from 'react-hook-form';

import { useFormGroupListener } from '@/hooks/useFormGroupListener';
import { useReviewMode } from '@/hooks/useReviewMode';
import { scrollToElement } from '@/lib/functions';

import { Block } from './Block';
import { type Event } from './FormGroup';

export const FormBlock = ({
  children,
  title,
  name,
  form,
  subtitle,
  lengthIndicator,
  preview,
  allowOverflow = false,
  reviewWarning = false,
  allowEdit = true,
  reviewWarningTooltip
}: {
  children: React.ReactNode;
  title: string;
  name: string;
  subtitle?: string;
  isValid?: boolean;
  form: UseFormReturn<any>;
  lengthIndicator?: string;
  preview?: React.ReactNode;
  allowOverflow?: boolean;
  reviewWarning?: boolean;
  allowEdit?: boolean;
  reviewWarningTooltip?: string;
}) => {
  const { isValid, errors } = useFormState({ control: form.control });
  const [isEditing, setEditing] = useState(false);

  useFormGroupListener(name, async (event: Event) => {
    if (event.type === 'validate') {
      const isValid = await form.trigger();
      return isValid;
    }

    const formFields = Object.keys(form.getValues());

    const formHasEventField = event.payload?.name && formFields.includes(event.payload.name);

    if (event.type === 'error' && formHasEventField) {
      setEditing(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const element = document.querySelector(`#${event?.payload?.name}, [name="${event?.payload?.name}"]`);
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });

      return true;
    }
  });

  const reviewMode = useReviewMode() && !isEditing;

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const invalidFields: (HTMLInputElement | HTMLTextAreaElement | null)[] = Object.entries(errors).map(([key]) => {
        return document.querySelector(`#${key}, [name="${key}"]`);
      });

      scrollToElement(invalidFields[0]);
    }
  }, [errors]);

  if (reviewMode && preview) {
    return (
      <Block
        allowEdit={allowEdit}
        isEditing={isEditing}
        title={title}
        reviewWarning={reviewWarning}
        onEdit={v => setEditing(v)}
        name={name}
        warningTooltip={reviewWarningTooltip}
      >
        {preview}
      </Block>
    );
  }

  return (
    <FormProvider {...form}>
      <Block
        isEditing={isEditing}
        isValid={isValid}
        onEdit={v => setEditing(v)}
        lengthIndicator={lengthIndicator}
        subtitle={subtitle}
        title={title}
        allowEdit={allowEdit}
        allowOverflow={allowOverflow}
        name={name}
      >
        {children}
      </Block>
    </FormProvider>
  );
};
